import * as R from 'ramda';
import * as Yup from 'yup';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
import { getGroupedByCountryStateOptionsByConfig } from '../../../helpers/options';
//////////////////////////////////////////////////

const settings = {
  [GC.FIELD_CONTACT_TYPE]: {
    type: 'select',
    name: 'titles:contact-type',
    options: GC.DRIVER_CONTACT_TYPE,
    customComponent: R.path(['data', GC.FIELD_CONTACT_TYPE, GC.FIELD_DISPLAYED_VALUE]),
    filter: {
      type: 'string',
      getFieldUiValue: R.path([GC.FIELD_CONTACT_TYPE, GC.FIELD_DISPLAYED_VALUE]),
    },
  },
  [GC.FIELD_ADDRESS]: {
    name: 'titles:address',
    type: 'addressAutocomplete',
    additionalInputWrapperStyles: { zIndex: 12 },
    locationMapper: (location) => ({
      ...location,
      [GC.FIELD_ADDRESS]: G.getPropFromObject(GC.FIELD_ADDRESS_1, location),
      [GC.FIELD_CONTACT_POSTAL_CODE]: G.getPropFromObject(GC.FIELD_ZIP, location),
    }),
  },
  [GC.FIELD_CITY]: {
    name: 'titles:city',
  },
  [GC.FIELD_STATE]: {
    width: 150,
    uiType: 'state',
    name: 'titles:state',
    filter: {
      type: 'selectMultipleCreatable',
      options: getGroupedByCountryStateOptionsByConfig,
    },
  },
  [GC.FIELD_CONTACT_POSTAL_CODE]: {
    width: 150,
    name: 'titles:postal-code',
  },
  [GC.FIELD_COUNTRY]: {
    width: 150,
    type: 'countrySelect',
    name: 'titles:country',
  },
  [GC.FIELD_EMAIL]: {
    name: 'titles:email',
  },
  [GC.FIELD_FLEET_MOBILE_PHONE]: {
    width: 150,
    name: 'titles:mobile-phone',
  },
  [GC.FIELD_HOME_PHONE]: {
    width: 150,
    name: 'titles:home-phone',
  },
  [GC.FIELD_EMERGENCY_CONTACT_NAME]: {
    name: 'titles:emergency-contact-name',
  },
  [GC.FIELD_EMERGENCY_PHONE]: {
    name: 'titles:emergency-phone',
  },
};

const validationSchema = Yup.object().shape({
  [GC.FIELD_EMAIL]: Yup.string()
    .notRequired()
    .nullable(true)
    .email(G.getShouldBeEmailLocaleTxt()),
  [GC.FIELD_HOME_PHONE]: Yup.string()
    .notRequired()
    .nullable(true)
    .matches(GC.PHONE_NUMBER_REGEXP, G.getFieldIsNotValidLocaleTxt()),
  [GC.FIELD_FLEET_MOBILE_PHONE]: Yup.string()
    .notRequired()
    .nullable(true)
    .matches(GC.PHONE_NUMBER_REGEXP, G.getFieldIsNotValidLocaleTxt()),
  [GC.FIELD_EMERGENCY_PHONE]: Yup.string()
    .nullable(true)
    .notRequired(G.getRequiredLocaleTxt())
    .matches(GC.PHONE_NUMBER_REGEXP, G.getFieldIsNotValidLocaleTxt()),
});

const defaultInputWrapperStyles = {
  mb: 25,
  width: 280,
};

const makeReportFields = (fields) => G.mapIndexed((name, sequence) => ({ name, sequence }), fields);

const defaultReportFields = R.keys(settings);
const defaultValues = R.map(() => null, settings);

const makeFieldSettings = R.compose(
  R.values,
  R.mapObjIndexed(({
    name,
    options,
    type = 'text',
    locationMapper,
    isRequired = false,
    additionalInputWrapperStyles,
  }, fieldName) => ({
    type,
    options,
    fieldName,
    isRequired,
    locationMapper,
    label: R.of(Array, name),
    inputWrapperStyles: {
      ...defaultInputWrapperStyles,
      ...G.spreadUiStyles(additionalInputWrapperStyles),
    },
  })),
);

const columnSettings = R.map(
  (field) => ({ ...R.pick(['name', 'width', 'filter', 'customComponent'], field), type: R.prop('uiType', field) }),
  settings,
);

export const contactSettings = {
  defaultValues,
  columnSettings,
  validationSchema,
  defaultReportFields,
  groupName: 'contacts',
  withResizableColumns: true,
  withUiFiltersAndEditView: true,
  fieldSettings: makeFieldSettings(settings),
  itemTitleArr: ['titles:contact', 'Contact'],
  formGroupTitleArr: ['titles:contacts', 'Contacts'],
  additionalTableSettings: { searchableTitles: true },
  report: {
    fields: makeReportFields(defaultReportFields),
    [GC.FIELD_TYPE]: GC.DRIVER_PROFILE_UI_REPORTS,
    [GC.FIELD_GUID]: `${GC.DRIVER_PROFILE_UI_REPORTS}Contacts`,
  },
  endpoints: {
    list: 'driverContactList',
    createOrUpdate: 'driverContact',
    remove: 'getCurrentContactEndpoint',
  },
  fieldsWrapperStyles: {
    width: 600,
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  // helpers
  makeOptionsForSelect: ({ configGroup }) => ({
    [GC.DRIVER_CONTACT_TYPE]: R.path(['dropdownOptions', GC.DRIVER_CONTACT_TYPE], configGroup),
  }),
  makeInitialValues: (initialValues) => R.assoc(
    GC.FIELD_CONTACT_TYPE,
    G.getDropdownOptionGuidFromObject(R.prop(GC.FIELD_CONTACT_TYPE, initialValues)),
    initialValues,
  ),
};

const vendorSettings = R.dissoc(GC.FIELD_CONTACT_TYPE, settings);

const vendorDefaultReportFields = R.keys(vendorSettings);

export const vendorContactSettings = {
  ...R.omit(['makeInitialValues', 'makeOptionsForSelect'], contactSettings),
  defaultReportFields: vendorDefaultReportFields,
  primaryObjectGuidKey: GC.FIELD_FLEET_VENDOR_GUID,
  fieldSettings: makeFieldSettings(vendorSettings),
  defaultValues: R.dissoc(GC.FIELD_CONTACT_TYPE, defaultValues),
  columnSettings: R.dissoc(GC.FIELD_CONTACT_TYPE, columnSettings),
  report: {
    [GC.FIELD_TYPE]: GC.VENDOR_PROFILE_UI_REPORTS,
    fields: makeReportFields(vendorDefaultReportFields),
    [GC.FIELD_GUID]: `${GC.VENDOR_PROFILE_UI_REPORTS}Contacts`,
  },
  endpoints: {
    list: 'vendorContactList',
    remove: 'removeVendorContact',
    createOrUpdate: 'vendorContact',
  },
  // helpers
  makeRequestPayload: ({ primaryObjectGuid }) => ({
    params: {
      [GC.FIELD_VENDOR_GUID]: primaryObjectGuid,
    },
  }),
};
